/**
 * Generates a UUID (Universally Unique Identifier) using the `crypto.randomUUID` method.
 *
 * @returns A string representing a UUID in the format `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`,
 *                   where `x` is a hexadecimal digit and `y` is one of `8`, `9`, `A`, or `B`.
 *
 * @example
 * const id = uuid();
 * console.log(id); // "550e8400-e29b-41d4-a716-446655440000"
 *
 * @remarks In the name of gods of testability, this function should not be used directly in test files.
 * Instead, use a simple string stub.
 */
export function uuid() {
  return crypto.randomUUID()
}

const UUID_REGEX =
  /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i

/**
 * Validates if a given string is a valid UUID.
 *
 * @param uuid - The string to validate.
 * @returns `true` if the string is a valid UUID, `false` otherwise.
 *
 * @example
 * console.log(isUUID("550e8400-e29b-41d4-a716-446655440000")); // true
 * console.log(isUUID("invalid-uuid")); // false
 */
export function isUUID(uuid: string) {
  return UUID_REGEX.test(uuid)
}

/**
 * Converts a UUID string to a hexadecimal representation.
 *
 * @param uuid - A valid UUID string in the format `xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`.
 * @param addLeadingZero - Whether to prepend `0x` to the hexadecimal string. (Default: `false`)
 * @returns The hexadecimal representation of the UUID.
 *
 * @throws {Error} If the provided string is not a valid UUID.
 *
 * @example
 * console.log(uuidToHex("550e8400-e29b-41d4-a716-446655440000")); // "550e8400e29b41d4a716446655440000"
 * console.log(uuidToHex("550e8400-e29b-41d4-a716-446655440000", true)); // "0x550e8400e29b41d4a716446655440000"
 */
export function uuidToHex(uuid: string, addLeadingZero = false) {
  if (!isUUID(uuid)) {
    throw new Error('Invalid UUID string.')
  }

  const hexString = uuid.replace(/-/g, '')

  return addLeadingZero ? `0x${hexString}` : hexString
}
